/**
 * Streaming hash engine — SHA-256, SHA-1, MD5, CRC32.
 *
 * Strategy:
 *   - Single-algo + small file (≤256 MB) → Web Crypto subtle.digest (fast).
 *   - Multi-algo OR large file               → pure JS streaming (one pass over
 *     the Blob in 1 MB chunks, all hashes updated together).
 *
 * Web Crypto refuses MD5; MD5 always uses the streaming JS engine.
 *
 * Surface: window.__DiskImage.Hash
 *   await Hash.hash(blob, algo, onProgress, signal) → hex
 *   await Hash.multiHash(blob, algos[], onProgress, signal) → { algo: hex, ... }
 *
 * Algos: 'sha-256', 'sha-1', 'md5', 'crc32'  (case-insensitive)
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.Hash) return;

    var CRC32 = w.__CRC32;

    var WEB_CRYPTO_THRESHOLD = 256 * 1024 * 1024;
    var STREAM_CHUNK = 1024 * 1024;

    function _toHex(arr) {
        var s = '';
        for (var i = 0; i < arr.length; i++) {
            var h = (arr[i] & 0xFF).toString(16);
            if (h.length < 2) h = '0' + h;
            s += h;
        }
        return s;
    }
    function _u32be(v) {
        return [(v >>> 24) & 0xFF, (v >>> 16) & 0xFF, (v >>> 8) & 0xFF, v & 0xFF];
    }

    /* ───── SHA-256 ───── */
    var SHA256_K = new Uint32Array([
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ]);
    function Sha256() {
        this.h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
        this.buf = new Uint8Array(64);
        this.bufLen = 0;
        this.totalLen = 0;
    }
    Sha256.prototype.update = function (bytes) {
        var off = 0;
        var len = bytes.length;
        this.totalLen += len;
        if (this.bufLen) {
            var need = 64 - this.bufLen;
            if (len < need) {
                this.buf.set(bytes, this.bufLen);
                this.bufLen += len;
                return this;
            }
            this.buf.set(bytes.subarray(0, need), this.bufLen);
            this._block(this.buf, 0);
            off = need;
            this.bufLen = 0;
        }
        while (off + 64 <= len) {
            this._block(bytes, off);
            off += 64;
        }
        if (off < len) {
            this.buf.set(bytes.subarray(off), 0);
            this.bufLen = len - off;
        }
        return this;
    };
    Sha256.prototype._block = function (b, off) {
        var W = new Uint32Array(64);
        for (var i = 0; i < 16; i++) {
            W[i] = (b[off + i * 4] << 24) | (b[off + i * 4 + 1] << 16) | (b[off + i * 4 + 2] << 8) | b[off + i * 4 + 3];
        }
        for (var t = 16; t < 64; t++) {
            var s0 = ((W[t - 15] >>> 7) | (W[t - 15] << 25)) ^ ((W[t - 15] >>> 18) | (W[t - 15] << 14)) ^ (W[t - 15] >>> 3);
            var s1 = ((W[t - 2] >>> 17) | (W[t - 2] << 15)) ^ ((W[t - 2] >>> 19) | (W[t - 2] << 13)) ^ (W[t - 2] >>> 10);
            W[t] = (W[t - 16] + s0 + W[t - 7] + s1) >>> 0;
        }
        var a = this.h[0], b1 = this.h[1], c = this.h[2], d = this.h[3];
        var e = this.h[4], f = this.h[5], g = this.h[6], hh = this.h[7];
        for (var t2 = 0; t2 < 64; t2++) {
            var S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
            var ch = (e & f) ^ ((~e) & g);
            var T1 = (hh + S1 + ch + SHA256_K[t2] + W[t2]) >>> 0;
            var S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
            var maj = (a & b1) ^ (a & c) ^ (b1 & c);
            var T2 = (S0 + maj) >>> 0;
            hh = g; g = f; f = e;
            e = (d + T1) >>> 0;
            d = c; c = b1; b1 = a;
            a = (T1 + T2) >>> 0;
        }
        this.h[0] = (this.h[0] + a) >>> 0;
        this.h[1] = (this.h[1] + b1) >>> 0;
        this.h[2] = (this.h[2] + c) >>> 0;
        this.h[3] = (this.h[3] + d) >>> 0;
        this.h[4] = (this.h[4] + e) >>> 0;
        this.h[5] = (this.h[5] + f) >>> 0;
        this.h[6] = (this.h[6] + g) >>> 0;
        this.h[7] = (this.h[7] + hh) >>> 0;
    };
    Sha256.prototype.digest = function () {
        var bitLen = this.totalLen * 8;
        var pad = (this.bufLen < 56) ? 56 - this.bufLen : 120 - this.bufLen;
        var tail = new Uint8Array(pad + 8);
        tail[0] = 0x80;
        // 64-bit BE bit length
        var hi = Math.floor(bitLen / 0x100000000);
        var lo = bitLen >>> 0;
        tail[pad + 0] = (hi >>> 24) & 0xFF;
        tail[pad + 1] = (hi >>> 16) & 0xFF;
        tail[pad + 2] = (hi >>> 8) & 0xFF;
        tail[pad + 3] = hi & 0xFF;
        tail[pad + 4] = (lo >>> 24) & 0xFF;
        tail[pad + 5] = (lo >>> 16) & 0xFF;
        tail[pad + 6] = (lo >>> 8) & 0xFF;
        tail[pad + 7] = lo & 0xFF;
        this.update(tail);
        var out = new Uint8Array(32);
        for (var i = 0; i < 8; i++) {
            out[i * 4] = (this.h[i] >>> 24) & 0xFF;
            out[i * 4 + 1] = (this.h[i] >>> 16) & 0xFF;
            out[i * 4 + 2] = (this.h[i] >>> 8) & 0xFF;
            out[i * 4 + 3] = this.h[i] & 0xFF;
        }
        return out;
    };

    /* ───── SHA-1 ───── */
    function Sha1() {
        this.h = new Uint32Array([0x67452301, 0xEFCDAB89, 0x98BADCFE, 0x10325476, 0xC3D2E1F0]);
        this.buf = new Uint8Array(64);
        this.bufLen = 0;
        this.totalLen = 0;
    }
    Sha1.prototype.update = Sha256.prototype.update;
    Sha1.prototype._block = function (b, off) {
        var W = new Uint32Array(80);
        for (var i = 0; i < 16; i++) {
            W[i] = (b[off + i * 4] << 24) | (b[off + i * 4 + 1] << 16) | (b[off + i * 4 + 2] << 8) | b[off + i * 4 + 3];
        }
        for (var t = 16; t < 80; t++) {
            var v = W[t - 3] ^ W[t - 8] ^ W[t - 14] ^ W[t - 16];
            W[t] = ((v << 1) | (v >>> 31)) >>> 0;
        }
        var a = this.h[0], b1 = this.h[1], c = this.h[2], d = this.h[3], e = this.h[4];
        for (var t2 = 0; t2 < 80; t2++) {
            var f, k;
            if (t2 < 20) { f = (b1 & c) | ((~b1) & d); k = 0x5A827999; }
            else if (t2 < 40) { f = b1 ^ c ^ d; k = 0x6ED9EBA1; }
            else if (t2 < 60) { f = (b1 & c) | (b1 & d) | (c & d); k = 0x8F1BBCDC; }
            else { f = b1 ^ c ^ d; k = 0xCA62C1D6; }
            var T = (((a << 5) | (a >>> 27)) + f + e + k + W[t2]) >>> 0;
            e = d; d = c; c = ((b1 << 30) | (b1 >>> 2)) >>> 0; b1 = a; a = T;
        }
        this.h[0] = (this.h[0] + a) >>> 0;
        this.h[1] = (this.h[1] + b1) >>> 0;
        this.h[2] = (this.h[2] + c) >>> 0;
        this.h[3] = (this.h[3] + d) >>> 0;
        this.h[4] = (this.h[4] + e) >>> 0;
    };
    Sha1.prototype.digest = function () {
        var bitLen = this.totalLen * 8;
        var pad = (this.bufLen < 56) ? 56 - this.bufLen : 120 - this.bufLen;
        var tail = new Uint8Array(pad + 8);
        tail[0] = 0x80;
        var hi = Math.floor(bitLen / 0x100000000);
        var lo = bitLen >>> 0;
        tail[pad + 0] = (hi >>> 24) & 0xFF;
        tail[pad + 1] = (hi >>> 16) & 0xFF;
        tail[pad + 2] = (hi >>> 8) & 0xFF;
        tail[pad + 3] = hi & 0xFF;
        tail[pad + 4] = (lo >>> 24) & 0xFF;
        tail[pad + 5] = (lo >>> 16) & 0xFF;
        tail[pad + 6] = (lo >>> 8) & 0xFF;
        tail[pad + 7] = lo & 0xFF;
        this.update(tail);
        var out = new Uint8Array(20);
        for (var i = 0; i < 5; i++) {
            out[i * 4] = (this.h[i] >>> 24) & 0xFF;
            out[i * 4 + 1] = (this.h[i] >>> 16) & 0xFF;
            out[i * 4 + 2] = (this.h[i] >>> 8) & 0xFF;
            out[i * 4 + 3] = this.h[i] & 0xFF;
        }
        return out;
    };

    /* ───── MD5 ───── */
    var MD5_R = [
        7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
        5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
        4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
        6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21
    ];
    var MD5_K = (function () {
        var k = new Uint32Array(64);
        for (var i = 0; i < 64; i++) k[i] = (Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0);
        return k;
    })();
    function Md5() {
        this.h = new Uint32Array([0x67452301, 0xEFCDAB89, 0x98BADCFE, 0x10325476]);
        this.buf = new Uint8Array(64);
        this.bufLen = 0;
        this.totalLen = 0;
    }
    Md5.prototype.update = Sha256.prototype.update;
    Md5.prototype._block = function (b, off) {
        var M = new Uint32Array(16);
        for (var i = 0; i < 16; i++) {
            M[i] = (b[off + i * 4] | (b[off + i * 4 + 1] << 8) | (b[off + i * 4 + 2] << 16) | (b[off + i * 4 + 3] << 24)) >>> 0;
        }
        var a = this.h[0], b1 = this.h[1], c = this.h[2], d = this.h[3];
        for (var t = 0; t < 64; t++) {
            var f, g;
            if (t < 16) { f = (b1 & c) | ((~b1) & d); g = t; }
            else if (t < 32) { f = (d & b1) | ((~d) & c); g = (5 * t + 1) % 16; }
            else if (t < 48) { f = b1 ^ c ^ d; g = (3 * t + 5) % 16; }
            else { f = c ^ (b1 | (~d)); g = (7 * t) % 16; }
            var temp = d;
            d = c; c = b1;
            var sum = (a + f + MD5_K[t] + M[g]) >>> 0;
            b1 = (b1 + (((sum << MD5_R[t]) | (sum >>> (32 - MD5_R[t]))) >>> 0)) >>> 0;
            a = temp;
        }
        this.h[0] = (this.h[0] + a) >>> 0;
        this.h[1] = (this.h[1] + b1) >>> 0;
        this.h[2] = (this.h[2] + c) >>> 0;
        this.h[3] = (this.h[3] + d) >>> 0;
    };
    Md5.prototype.digest = function () {
        var bitLen = this.totalLen * 8;
        var pad = (this.bufLen < 56) ? 56 - this.bufLen : 120 - this.bufLen;
        var tail = new Uint8Array(pad + 8);
        tail[0] = 0x80;
        var hi = Math.floor(bitLen / 0x100000000);
        var lo = bitLen >>> 0;
        // MD5 length is little-endian 64-bit
        tail[pad + 0] = lo & 0xFF;
        tail[pad + 1] = (lo >>> 8) & 0xFF;
        tail[pad + 2] = (lo >>> 16) & 0xFF;
        tail[pad + 3] = (lo >>> 24) & 0xFF;
        tail[pad + 4] = hi & 0xFF;
        tail[pad + 5] = (hi >>> 8) & 0xFF;
        tail[pad + 6] = (hi >>> 16) & 0xFF;
        tail[pad + 7] = (hi >>> 24) & 0xFF;
        this.update(tail);
        var out = new Uint8Array(16);
        for (var i = 0; i < 4; i++) {
            out[i * 4] = this.h[i] & 0xFF;
            out[i * 4 + 1] = (this.h[i] >>> 8) & 0xFF;
            out[i * 4 + 2] = (this.h[i] >>> 16) & 0xFF;
            out[i * 4 + 3] = (this.h[i] >>> 24) & 0xFF;
        }
        return out;
    };

    /* ───── CRC32 streaming wrapper ───── */
    function Crc32() {
        this.crc = 0;
        this.totalLen = 0;
    }
    Crc32.prototype.update = function (bytes) {
        if (!CRC32) throw new Error('CRC32 helper missing — load checksum-table.js first');
        this.crc = CRC32.bytes(this.crc, bytes);
        this.totalLen += bytes.length;
        return this;
    };
    Crc32.prototype.digest = function () {
        return new Uint8Array([(this.crc >>> 24) & 0xFF, (this.crc >>> 16) & 0xFF, (this.crc >>> 8) & 0xFF, this.crc & 0xFF]);
    };

    function _newHasher(algo) {
        algo = (algo || '').toLowerCase();
        switch (algo) {
            case 'sha-256': case 'sha256': return new Sha256();
            case 'sha-1': case 'sha1': return new Sha1();
            case 'md5': return new Md5();
            case 'crc32': return new Crc32();
        }
        throw new Error('Unsupported algo: ' + algo);
    }

    function _normalize(algo) {
        algo = (algo || '').toLowerCase();
        if (algo === 'sha256') return 'sha-256';
        if (algo === 'sha1') return 'sha-1';
        return algo;
    }

    function _streamPureJs(blob, algos, onProgress, signal) {
        var hashers = {};
        algos.forEach(function (a) { hashers[_normalize(a)] = _newHasher(a); });
        var off = 0;
        var total = blob.size;
        function step() {
            if (signal && signal.aborted) return Promise.reject(new DOMException('aborted', 'AbortError'));
            if (off >= total) {
                var out = {};
                Object.keys(hashers).forEach(function (k) { out[k] = _toHex(hashers[k].digest()); });
                if (onProgress) onProgress({ done: true, processed: total, total: total });
                return out;
            }
            var end = Math.min(total, off + STREAM_CHUNK);
            return blob.slice(off, end).arrayBuffer().then(function (ab) {
                var bytes = new Uint8Array(ab);
                Object.keys(hashers).forEach(function (k) { hashers[k].update(bytes); });
                off = end;
                if (onProgress) onProgress({ processed: off, total: total, ratio: total ? (off / total) : 1 });
                return step();
            });
        }
        return Promise.resolve(step());
    }

    function _webCrypto(blob, algo) {
        var name = _normalize(algo).toUpperCase();
        if (!w.crypto || !w.crypto.subtle || !w.crypto.subtle.digest) return Promise.reject(new Error('no-subtle'));
        return blob.arrayBuffer().then(function (ab) {
            return w.crypto.subtle.digest(name, ab);
        }).then(function (h) {
            return _toHex(new Uint8Array(h));
        });
    }

    function hash(blob, algo, onProgress, signal) {
        var n = _normalize(algo);
        if (n !== 'md5' && n !== 'crc32' && blob.size <= WEB_CRYPTO_THRESHOLD) {
            return _webCrypto(blob, n).then(function (hex) {
                if (onProgress) onProgress({ done: true, processed: blob.size, total: blob.size });
                var o = {}; o[n] = hex; return hex;
            }).catch(function () {
                return _streamPureJs(blob, [n], onProgress, signal).then(function (o) { return o[n]; });
            });
        }
        return _streamPureJs(blob, [n], onProgress, signal).then(function (o) { return o[n]; });
    }

    function multiHash(blob, algos, onProgress, signal) {
        if (!algos || !algos.length) return Promise.resolve({});
        return _streamPureJs(blob, algos, onProgress, signal);
    }

    w.__DiskImage.Hash = {
        hash: hash,
        multiHash: multiHash,
        Sha256: Sha256,
        Sha1: Sha1,
        Md5: Md5,
        Crc32: Crc32,
        WEB_CRYPTO_THRESHOLD: WEB_CRYPTO_THRESHOLD
    };
})(window);
